/**
 * 약관 타입별 표시명과 다이얼로그 타입 매핑 상수
 */

import type { ResponseTermsListRequiredType, ResponseTermsListOptionalType } from '@template/api';

/** 약관 타입 (필수/선택 약관 - 회원가입용) */
export type TermsType = ResponseTermsListRequiredType | ResponseTermsListOptionalType;

/** 전체 약관 타입 (회원가입용 + Open API 전용) */
export type AllTermsType = TermsType | 'OPEN_API_TERMS_OF_USE';

/** 오픈 API 약관 타입 */
export type TermsTypeForOpenApi = 'PERSONAL_INFO_COLLECTION' | 'OPEN_API_TERMS_OF_USE';

/**
 * 약관 정보 인터페이스
 */
export interface TermsInfo {
  /** 표시될 약관 이름 */
  label: string;
  /** 다이얼로그에서 사용할 탭 키 */
  dialogKey: string;
}

/**
 * 약관 타입별 정보 매핑
 */
export const TERMS_INFO_MAP: Record<AllTermsType, TermsInfo> = {
  TERMS_OF_USE: {
    label: '이용약관',
    dialogKey: 'terms-of-use',
  },
  OPEN_API_TERMS_OF_USE: {
    label: 'Open API 이용약관',
    dialogKey: 'open-api-terms-of-use',
  },
  PERSONAL_INFO_COLLECTION: {
    label: '개인정보 처리방침',
    dialogKey: 'privacy-policy',
  },
  RISK_DISCLOSURE: {
    label: '위험 공시 안내',
    dialogKey: 'risk-disclosure',
  },
  FAIS_DISCLOSURE: {
    label: 'FAIS 공시 안내',
    dialogKey: 'fais-disclosure',
  },
  POPIA_POLICY: {
    label: 'POPIA 정책',
    dialogKey: 'popia-policy',
  },
  MARKETING_NOTIFICATION: {
    label: '마케팅 정보수신 동의',
    dialogKey: 'marketing',
  },
};

/**
 * 약관 타입의 표시명을 반환합니다
 * @param termsType - 약관 타입
 * @returns 표시명
 */
export const getTermsLabel = (termsType: AllTermsType): string => {
  return TERMS_INFO_MAP[termsType]?.label || termsType;
};

/**
 * 약관 타입의 다이얼로그 키를 반환합니다
 * @param termsType - 약관 타입
 * @returns 다이얼로그 키
 */
export const getTermsDialogKey = (termsType: AllTermsType): string => {
  return TERMS_INFO_MAP[termsType]?.dialogKey || termsType.toLowerCase();
};

/**
 * 다이얼로그 키로부터 약관 타입을 반환합니다
 * @param dialogKey - 다이얼로그 키
 * @returns 약관 타입 또는 undefined
 */
export const getTermsTypeFromDialogKey = (dialogKey: string): AllTermsType | undefined => {
  const entry = Object.entries(TERMS_INFO_MAP).find(([_, info]) => info.dialogKey === dialogKey);
  return entry ? (entry[0] as AllTermsType) : undefined;
};
